import * as angular from 'angular';
import * as _ from 'lodash';

import { IFormatNumberHelper, IValueFormattingService } from '..';
import {
    WidgetValues,
    TagDateValue,
    IValueViewModel,
    KeyValuePair,
    ResultsStatus,
    ITileSerieConfiguration,
    Dictionary,
    AggregationMode,
    DigitalSetSimpleDictionary,
    TagStringValueDto
} from '../../models';
import { ISeriesBuilder, SeriesMappingResult, ISeriesBuilderArguments } from '../../services/widgets';
import { mapConfiguration, getUnitName } from '../../common';
import { getAlarmClass } from '../../widgets/common/getAlarmClass';
import { ITileViewModel } from './ITileViewModel';
import { TileViewModel } from './TileViewModel';
import { TileConfiguration } from 'widgets/tileWidget/tileWidget.configuration';
import { formatValueForCsv } from './formatValueForCsv';

export interface ITileWidgetService extends ISeriesBuilder<ITileSerieConfiguration> {
    getTileViewModels(
        series: ITileSerieConfiguration[],
        data: WidgetValues,
        viewModels: Dictionary<IValueViewModel>,
        digitalSets: DigitalSetSimpleDictionary,
        configuration: { aggregationMode: AggregationMode; dateFormat?: string },
        height?: number,
        relativeWidth?: number,
        stringValues?: TagStringValueDto[]
    ): ITileViewModel[];
    buildCsvTable(
        configuration: TileConfiguration,
        data: WidgetValues,
        viewModels: Dictionary<IValueViewModel>,
        digitalSets: DigitalSetSimpleDictionary,
        stringValues?: TagStringValueDto[]
    ): string[][];
}

export class TileWidgetService implements ITileWidgetService {
    static serviceId = 'tileWidgetService';

    emptyName: string = '-';

    static $inject = ['$q', 'formatNumberHelper', 'valueFormattingService'];
    constructor(private _$q: angular.IQService, private _formatNumberHelper: IFormatNumberHelper, private _valueFormattingService: IValueFormattingService) {}

    getTileViewModels(
        series: ITileSerieConfiguration[],
        data: WidgetValues,
        viewModels: Dictionary<IValueViewModel>,
        digitalSets: DigitalSetSimpleDictionary,
        configuration: { aggregationMode: AggregationMode; dateFormat?: string },
        height: number = 0,
        relativeWidth: number = 0,
        stringValues?: TagStringValueDto[]
    ): ITileViewModel[] {
        return series
            ? series.map((serie) => {
                  const vm = viewModels ? viewModels[serie.guid] : null;
                  const { formattedValue, actualValue } = this.getFormatedValue(serie, data, vm, digitalSets, configuration, stringValues);
                  const retval = new TileViewModel(
                      formattedValue,
                      serie.guid,
                      serie.decimals,
                      serie.background,
                      serie.foreground,
                      Math.ceil((serie.relativeFontSize ? serie.relativeFontSize : 1) * height),
                      relativeWidth,
                      this.getAlarmForSerie(serie, data.alarms),
                      vm,
                      serie.selection
                  );
                  retval.actualValue = actualValue;
                  return retval;
              })
            : [];
    }

    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult> {
        const { configuration, viewModels, map, errors } = args;
        const mapped = mapConfiguration(configuration.series as any[], viewModels, map, errors);
        const error = mapped.error;

        const series = mapped.series.filter((s) => !s.missing).map((r) => this.mapSerieToVM(r.viewModel, r.configuration as any));

        return this._$q.when({
            error,
            series: series,
            map
        });
    }

    buildCsvTable(
        configuration: TileConfiguration,
        data: WidgetValues,
        viewModels: Dictionary<IValueViewModel>,
        digitalSets: DigitalSetSimpleDictionary,
        stringValues?: TagStringValueDto[]
    ) {
        const table: string[][] = [];
        table.push(this.getHeadings(configuration));
        const formatter = this._formatNumberHelper.getFormatter(null, false);
        const values = this.getTileViewModels(
            configuration.series,
            data,
            viewModels,
            digitalSets,
            { aggregationMode: configuration.aggregationMode || AggregationMode.Average },
            undefined,
            undefined,
            stringValues
        ).map((d) => (d.actualValue ? formatValueForCsv(d.actualValue, formatter) : d.value));
        table.push(values);
        return table;
    }

    private getHeadings(configuration: TileConfiguration): string[] {
        return configuration.series.map((s) => (s.selection === 'value' ? `${s.selection} (${s.valueType})` : s.selection));
    }

    private mapSerieToVM(vm: IValueViewModel, serie: ITileSerieConfiguration) {
        const result = angular.copy(serie);
        result.guid = vm.guid;
        return result;
    }

    private getAlarmForSerie(serie: ITileSerieConfiguration, alarms: KeyValuePair<string, ResultsStatus>[]): string {
        let result = '';
        if (serie.showAlarm && alarms && alarms.length > 0) {
            const concerningAlarms = alarms.filter((a) => a.key === serie.guid).map((a) => a.value);
            if (concerningAlarms.length > 0) {
                result = getAlarmClass(_.maxBy(concerningAlarms));
            }
        }
        return result;
    }

    /**
     * Format the data based on the serie's selection
     * @param serie Serie configuration
     * @param data data to be displayed, only used for 'value' selection
     */
    private getFormatedValue(
        serie: ITileSerieConfiguration,
        data: WidgetValues,
        vm: IValueViewModel,
        digitalSets: DigitalSetSimpleDictionary,
        configuration: { aggregationMode: AggregationMode; dateFormat?: string },
        stringValues?: TagStringValueDto[]
    ): { formattedValue: string; actualValue?: number } {
        let formattedValue: string = this.emptyName;
        let actualValue: number = null;
        if (serie.selection) {
            switch (serie.selection) {
                case 'groupName':
                    formattedValue = vm ? vm.groupUniqueName : this.emptyName;
                    break;
                case 'name':
                    formattedValue = vm ? vm.name : this.emptyName;
                    break;
                case 'unitName':
                    formattedValue = vm ? getUnitName(vm.unitName, serie.valueType) : this.emptyName;
                    break;
                case 'value':
                    formattedValue = this.emptyName;
                    if (serie.valueType && data?.aggregatedValue) {
                        const foundValue = data.aggregatedValue.find((v: TagDateValue) => v.guid === serie.guid && v.valueType === serie.valueType);
                        if (foundValue && !isNaN(foundValue.value)) {
                            actualValue = foundValue.value;
                            formattedValue = this._valueFormattingService.formatValue(
                                foundValue,
                                vm,
                                digitalSets,
                                configuration.aggregationMode,
                                serie.decimals,
                                configuration.dateFormat,
                                stringValues
                            );
                            // formattedValue = this._formatNumberHelper.getFormatter(serie.decimals).format(actualValue);
                        }
                    }
                    break;
            }
        }
        return { formattedValue, actualValue };
    }
}
